import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Share, Plus } from 'lucide-react';
import { Button } from './ui/button';

const InstallAppPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user has dismissed before
      const dismissed = localStorage.getItem('installPromptDismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      
      if (!dismissed || dismissedTime < oneWeekAgo) {
        setTimeout(() => setShowPrompt(true), 5000); // Show after 5 seconds
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show custom prompt
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('iosInstallPromptDismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      
      if (!dismissed || dismissedTime < oneWeekAgo) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    }

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
        })
        .catch((error) => {
        });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    if (isIOS) {
      localStorage.setItem('iosInstallPromptDismissed', Date.now().toString());
    } else {
      localStorage.setItem('installPromptDismissed', Date.now().toString());
    }
    setShowPrompt(false);
  };

  // Don't show if already installed
  if (isStandalone) return null;

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[9998]"
          data-testid="install-app-prompt"
        >
          <div className="bg-[var(--background-paper)] rounded-2xl shadow-2xl border border-[var(--border-primary)] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#D4AF37] to-[#B8860B] p-4 flex items-center gap-3">
              <div className="p-2 bg-black/20 rounded-xl">
                <Smartphone className="w-6 h-6 text-black" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-black">Install NC Help Hub</h3>
                <p className="text-black/70 text-sm">Add to your home screen</p>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-2 hover:bg-black/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-black/70" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {isIOS ? (
                // iOS Instructions
                <div className="space-y-3">
                  <p className="text-[var(--text-secondary)] text-sm">
                    Install this app on your iPhone:
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-2 bg-[var(--background-subtle)] rounded-lg">
                      <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                        <Share className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      <span className="text-sm text-[var(--text-primary)]">
                        1. Tap the <strong>Share</strong> button
                      </span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-[var(--background-subtle)] rounded-lg">
                      <div className="w-8 h-8 bg-[var(--success)]/15 rounded-lg flex items-center justify-center">
                        <Plus className="w-4 h-4 text-[var(--success)]" />
                      </div>
                      <span className="text-sm text-[var(--text-primary)]">
                        2. Tap <strong>Add to Home Screen</strong>
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={handleDismiss}
                    variant="outline"
                    className="w-full mt-2"
                  >
                    Got it!
                  </Button>
                </div>
              ) : (
                // Android/Desktop Install
                <div className="space-y-3">
                  <p className="text-[var(--text-secondary)] text-sm">
                    Get quick access to NC Help Hub right from your home screen. No app store needed!
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleInstall}
                      className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#B8860B] hover:to-[#D4AF37] text-black font-semibold"
                      data-testid="install-app-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Install App
                    </Button>
                    <Button
                      onClick={handleDismiss}
                      variant="outline"
                      className="text-[var(--text-muted)]"
                    >
                      Later
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallAppPrompt;
